import {
  CotasDeFundoApiResponse,
  CadastroCotasDeFundoApiRequest,
  InstrumentoFinanceiro,
  TipoRegistroInstrumentoFinanceiro,
  ReservarUltimoNumeroDaEmissaoRequest,
} from 'common/genericTypes';
import FormularioCotasDeFundo from 'pages/cotasDeFundo/registro/formulario';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  resetCotasDeFundoDetails,
  updateCotasDeFundoCaracteristicas,
  updateField,
  resetMetadata,
} from 'store/modules/cotasDeFundo/caracteristicas';
import CotasDeFundoParser from 'utils/parsers/cotasDeFundoParser';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors, typeOfRegister } from 'store/modules/cotasDeFundo';
import { validaCpfCnpj } from 'utils/validation';

type ComponentParams = { cotasDeFundoId?: string };
type ComponentState = { cotasDeFundoRascunho?: CotasDeFundoApiResponse; acao?: TipoRegistroInstrumentoFinanceiro };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const CotasDeFundo: React.FC<Props> = (props) => {
  const { cotasDeFundoId } = props.match.params;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const caracteristicas = useSelector((state: RootState) => state.cotasDeFundoCadastro.caracteristicas);
  const uploadDocumentos = useSelector((state: RootState) => state.cotasDeFundoCadastro.uploadDocumentos);

  const cotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');

  const submitPutCotasDeFundo = async (cotasDeFundoId: string, cotasDeFundo: CadastroCotasDeFundoApiRequest) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await cotasDeFundoApi.putCotasDeFundo(dispatch, cotasDeFundoId, cotasDeFundo, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) {}
    return result;
  };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjFundo || !validaCpfCnpj(caracteristicas.cnpjFundo)) {
      dispatch(addAlert({ message: 'CNPJ do fundo inválido', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjFundo,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.CotasDeFundo,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  interface ExtendedCotasDeFundoApiResponse extends CotasDeFundoApiResponse {
    hasError?: boolean;
  }

  const handleSubmit = async (): Promise<ExtendedCotasDeFundoApiResponse> => {
    try {
      const cotasDeFundo: CadastroCotasDeFundoApiRequest =
        CotasDeFundoParser.stateToCadastroCotasDeFundoApiRequest(caracteristicas);
      const created = await cotasDeFundoApi.postCotasDeFundo(dispatch, cotasDeFundo, true);

      if (created?.cotasDeFundoId) {
        for (const document of uploadDocumentos.documents) {
          if (document.file) {
            await cotasDeFundoApi.putUploadDoc(
              dispatch,
              created.cotasDeFundoId,
              document.type,
              document.file,
              true
            );
          }
        }
      }

      return created;
    } catch (error) {
      return { hasError: true } as CotasDeFundoApiResponse & { hasError: boolean };
    }
  };

  const reserveBeforeHandleSubmit = async (): Promise<ExtendedCotasDeFundoApiResponse> => {
    const numeroEmissaoReservado = await reservarNumeroEmissao();
    if (!numeroEmissaoReservado) return { hasError: true } as ExtendedCotasDeFundoApiResponse;
    return await handleSubmit();
  };
  const submitPostCotasDeFundo = async (): Promise<ExtendedCotasDeFundoApiResponse> => {
    return await reserveBeforeHandleSubmit();
  };



  const handleEditSubmit = async () => {
    if (!cotasDeFundoId) return { codigo: '', ticket: '' };

    const cotasDeFundo: CadastroCotasDeFundoApiRequest =
      CotasDeFundoParser.stateToCadastroCotasDeFundoApiRequest(caracteristicas);

    const cotasDeFundoResponse = await submitPutCotasDeFundo(cotasDeFundoId, cotasDeFundo);
    if (!cotasDeFundoResponse) return undefined;

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        await cotasDeFundoApi.putUploadDoc(
          dispatch,
          cotasDeFundoId,
          document.type,
          document.file,
          true
        );
      }
    }

    for (const documentToDelete of uploadDocumentos.documentsToDelete) {
      if (documentToDelete.remoteFileInfo?.id) {
        await cotasDeFundoApi.deleteUploadDoc(
          dispatch,
          cotasDeFundoId,
          documentToDelete.remoteFileInfo.id,
          true
        );
      }
    }

    return cotasDeFundoResponse;
  };

  const handleRegisterSubmit = async (): Promise<{ codigo: string; ticket: string; hasError?: boolean } | undefined> => {
    const uploadedCotasDeFundo = await submitPostCotasDeFundo();
    if (!uploadedCotasDeFundo) return undefined;
    if (uploadedCotasDeFundo.hasError) {
      return { codigo: '', ticket: '', hasError: true };
    }
    return { codigo: '', ticket: uploadedCotasDeFundo.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string; ticket: string } | undefined> => {
    try {
      if (codigo) {
        return { codigo, ticket: '' };
      } else throw 'ID das Cotas de Fundo não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };

  const handleSalvarRascunho = async () => {
    return '';
  };

  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(
    TipoRegistroInstrumentoFinanceiro.Cadastro
  );

  const defineTypeOfRegister = async (): Promise<void> => {
    if (cotasDeFundoId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetMetadata());
    dispatch(resetCotasDeFundoDetails());
    dispatch(highlightErrors(false));
    defineTypeOfRegister();
  }, []);

  useEffect(() => {
    if (cotasDeFundoId) {
      cotasDeFundoApi
        .getCotasDeFundoDetalhes(dispatch, cotasDeFundoId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateCotasDeFundoCaracteristicas(CotasDeFundoParser.apiResponseToCaracteristicasState(response)));
          }
        })
        .catch(() => {});
    }
    defineTypeOfRegister();
  }, [cotasDeFundoApi, cotasDeFundoId, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <FormularioCotasDeFundo
              cotasDeFundoId={cotasDeFundoId}
              title={`Cadastro Cotas de Fundo`}
              handleSubmit={handleRegisterSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <FormularioCotasDeFundo
              cotasDeFundoId={cotasDeFundoId}
              title={`Editar Cotas de Fundo`}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <FormularioCotasDeFundo
              cotasDeFundoId={cotasDeFundoId}
              title={`Editar ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <FormularioCotasDeFundo
              cotasDeFundoId={cotasDeFundoId}
              title={`Aditar Cotas de Fundo ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>;
    }
  };
  return component();
};

export default CotasDeFundo;
